/**
 * Create bundle dropdowns for optional products
 * This function is reusable across product pages and wishlist cards
 *
 * IMPORTANT: Two HTML templates are supported:
 * - 'table': WooCommerce-style table with reset link (for product pages)
 * - 'simple': Simple div wrapper like variable products (for wishlist cards)
 *
 * @param {jQuery} $container - Container to search for checkboxes
 * @param {jQuery} $dropdownTarget - Element where dropdown will be inserted
 * @param {Object} options - Configuration options
 * @param {Function} options.onCheckboxChange - Callback when checkbox changes
 * @param {string} options.mode - 'prepend' or 'append'
 * @param {string} options.htmlTemplate - 'table' (default, for product pages) or 'simple' (for wishlists)
 * @returns {Object} API object with methods to interact with dropdowns
 */
export function createBundleDropdowns($container, $dropdownTarget, options) {
  options = options || {}
  const mode = options.mode || 'prepend'
  const htmlTemplate = options.htmlTemplate || 'table'
  const onCheckboxChange = options.onCheckboxChange || function() {}

  let $allCheckboxes = $container.find('input.bundled_product_checkbox[name^="bundle_selected_optional"]')

  if ($allCheckboxes.length === 0) {
    return null
  }

  let $lastCheckbox = $allCheckboxes.last()
  let lastCheckboxName = $lastCheckbox.attr('name')
  let lastItemId = lastCheckboxName.replace('bundle_selected_optional_', '')

  // Get dropdown title for the last item
  let lastItemDropdownTitle = (window.bundleCustomData && window.bundleCustomData[lastItemId] && window.bundleCustomData[lastItemId].dropdown_title) ? window.bundleCustomData[lastItemId].dropdown_title : ''

  // Find all items with the same dropdown_title (group detection)
  let groupItems = []
  $allCheckboxes.each(function() {
    let itemId = $(this).attr('name').replace('bundle_selected_optional_', '')
    let itemDropdownTitle = (window.bundleCustomData && window.bundleCustomData[itemId] && window.bundleCustomData[itemId].dropdown_title) ? window.bundleCustomData[itemId].dropdown_title : ''

    if (itemDropdownTitle && itemDropdownTitle === lastItemDropdownTitle) {
      let itemCustomData = (window.bundleCustomData && window.bundleCustomData[itemId]) ? window.bundleCustomData[itemId] : {}

      // Find product title: check siblings first (wishlist cards), then use closest (product pages)
      let $productTitleElement = $(this).siblings('.bundled_product').find('.product_title')
      if (!$productTitleElement.length) {
        $productTitleElement = $(this).closest('.bundled_product').find('.product_title')
      }
      let productTitle = $productTitleElement.text().trim()

      groupItems.push({
        id: itemId,
        checkbox: $(this),
        productTitle: productTitle,
        customData: itemCustomData
      })
    }
  })

  // Decide which mode to use: RADIO (multiple items) or TOGGLE (single item)
  if (groupItems.length > 1) {
    return buildRadioModeDropdown(groupItems, lastItemDropdownTitle, lastItemId, $dropdownTarget, mode, htmlTemplate, onCheckboxChange)
  } else {
    return buildToggleModeDropdown($lastCheckbox, $allCheckboxes, lastItemId, $dropdownTarget, mode, htmlTemplate, onCheckboxChange)
  }
}

/* ============================================================================
 * RADIO MODE: Multiple items with same dropdown_title = choice between products
 * Example: "Avec Crème A" OR "Avec Crème B" OR "Sans produit"
 * ============================================================================ */
function buildRadioModeDropdown(groupItems, lastItemDropdownTitle, lastItemId, $dropdownTarget, mode, htmlTemplate, onCheckboxChange) {
  let dropdownTitle = lastItemDropdownTitle || 'Produit optionnel'
  let withoutProductLabel = groupItems[0].customData.without_product_label || ''
  let selectId = 'produit_optionnel_' + lastItemId
  let selectName = 'bundle_attribute_produit_optionnel_' + lastItemId

  // Build options HTML
  let optionsHtml = ``
  let defaultSelected = false
  let hasExplicitDefault = false

  // Check if any item has is_default_selected
  groupItems.forEach(function(item) {
    if (item.customData.is_default_selected) {
      hasExplicitDefault = true
    }
  })

  // Only add "none" option if withoutProductLabel is provided
  if (withoutProductLabel) {
    let noneSelected = !hasExplicitDefault ? ' selected' : ''
    optionsHtml += `<option value="none"${noneSelected}>${withoutProductLabel}</option>`
    if (!hasExplicitDefault) {
      defaultSelected = true
    }
  }

  groupItems.forEach(function(item, index) {
    let optionLabel = item.customData.with_product_label || item.productTitle
    optionLabel = optionLabel.replace('[nom du produit]', item.productTitle)
    let selected = ''
    if (item.customData.is_default_selected) {
      selected = ' selected'
      defaultSelected = true
    } else if (!defaultSelected && !hasExplicitDefault && index === 0 && !withoutProductLabel) {
      selected = ' selected'
      defaultSelected = true
    }
    optionsHtml += `<option value="${item.id}"${selected}>${optionLabel}</option>`
  })

  // ========================================================================
  // HTML TEMPLATE GENERATION
  // Two different templates depending on context:
  // ========================================================================

  let dropdownElement, $select, $resetLink

  if (htmlTemplate === 'simple') {
    // ======================================================================
    // SIMPLE TEMPLATE (for wishlist cards)
    // Generates clean HTML matching variable product variations
    // No table, no reset link - just label + select
    // ======================================================================
    dropdownElement = $(`
      <div class="card-wishlist__variation-select">
        <label for="${selectId}">${dropdownTitle}</label>
        <select id="${selectId}" name="${selectName}">
          ${optionsHtml}
        </select>
      </div>
    `)

    $dropdownTarget[mode](dropdownElement)
    $select = dropdownElement.find('select')
    $resetLink = null // No reset link in simple template

  } else {
    // ======================================================================
    // TABLE TEMPLATE (for product pages)
    // Generates WooCommerce-style table with reset link
    // Keeps original product page aesthetics
    // ======================================================================
    dropdownElement = $(`
      <table class="variations" cellspacing="0" role="presentation">
      <tbody>
      <tr class="attribute_options attribute_value_configurable" data-attribute_label="${dropdownTitle}">
      <td class="label">
      <label for="${selectId}">
      ${dropdownTitle}
      </label>
      </td>
      <td class="value">
      <select id="${selectId}" class="" name="${selectName}" data-attribute_name="attribute_produit_optionnel" data-show_option_none="yes">
      ${optionsHtml}
      </select>
      <div class="reset_bundled_variations_fixed">
      <a class="reset_variations" href="#">Effacer</a>
      </div>
      </td>
      </tr>
      </tbody>
      </table>
    `)

    $dropdownTarget[mode](dropdownElement)
    $select = dropdownElement.find('select')
    $resetLink = dropdownElement.find('.reset_variations')
  }

  // ========================================================================
  // EVENT HANDLERS (same for both templates)
  // ========================================================================

  // When dropdown changes, update checkboxes (only one checked at a time)
  $select.on('change', function() {
    var selectedValue = $(this).val()

    // Step 1: Uncheck all items and trigger change on each that was checked
    groupItems.forEach(function(item) {
      if (item.checkbox.is(':checked')) {
        item.checkbox.prop('checked', false).trigger('change')
      }
    })

    // Step 2: Check the selected item and trigger change on it
    if (selectedValue && selectedValue !== 'none') {
      groupItems.forEach(function(item) {
        if (item.id === selectedValue) {
          setTimeout(function() {
            item.checkbox.prop('checked', true).trigger('change')
            // Call callback AFTER checkbox is checked to ensure price calculation is correct
            onCheckboxChange()
          }, 15)
        }
      })
    } else {
      // No item selected (none), call callback immediately
      onCheckboxChange()
    }
  })

  // Reset link (only for table template)
  if ($resetLink) {
    $resetLink.on('click', function(e) {
      e.preventDefault()
      $select.val('none').trigger('change')
    })
  }

  // Set initial state: check the appropriate checkbox and update dropdown
  let checkedItem = groupItems.find(function(item) {
    return item.checkbox.is(':checked')
  })

  if (checkedItem) {
    $select.val(checkedItem.id)
  } else {
    // No checkbox is checked, so check the default selected option
    let selectedValue = $select.val()
    if (selectedValue && selectedValue !== 'none') {
      groupItems.forEach(function(item) {
        if (item.id === selectedValue) {
          item.checkbox.prop('checked', true)
        }
      })
    }
  }

  // When any checkbox in group changes, update the dropdown
  groupItems.forEach(function(item) {
    item.checkbox.on('change', function() {
      if ($(this).is(':checked')) {
        // This item was checked, set dropdown to it
        $select.val(item.id)
      } else {
        // Check if any other item is checked
        let anyChecked = groupItems.some(function(otherItem) {
          return otherItem.checkbox.is(':checked')
        })

        if (!anyChecked) {
          $select.val('none')
        }
      }
    })
  })

  return {
    $select: $select,
    $element: dropdownElement,
    mode: 'radio',
    groupItems: groupItems
  }
}

/* ============================================================================
 * TOGGLE MODE: Single optional item (avec/sans)
 * Example: "Avec produit X" OR "Sans produit X"
 * ============================================================================ */
function buildToggleModeDropdown($lastCheckbox, $allCheckboxes, lastItemId, $dropdownTarget, mode, htmlTemplate, onCheckboxChange) {
  // Find product title: check siblings first (wishlist cards), then use closest (product pages)
  let $productTitleElement = $lastCheckbox.siblings('.bundled_product').find('.product_title')
  if (!$productTitleElement.length) {
    $productTitleElement = $lastCheckbox.closest('.bundled_product').find('.product_title')
  }
  let productTitle = $productTitleElement.text().trim()

  let selectId = 'produit_optionnel_' + lastItemId
  let selectName = 'bundle_attribute_produit_optionnel_' + lastItemId

  // Default labels
  let dropdownTitle = 'Produit optionnel'
  let withProductLabel = 'Avec ' + productTitle
  let withoutProductLabel = 'Sans ' + productTitle
  let isDefaultSelected = false

  // Check if we have custom data from PHP
  if (window.bundleCustomData && window.bundleCustomData[lastItemId]) {
    const customData = window.bundleCustomData[lastItemId]

    // Use custom labels if they exist, otherwise keep defaults
    if (customData.dropdown_title) {
      dropdownTitle = customData.dropdown_title
    }

    if (customData.with_product_label) {
      withProductLabel = customData.with_product_label
      // Replace [nom du produit] placeholder if present
      withProductLabel = withProductLabel.replace('[nom du produit]', productTitle)
    }

    if (customData.without_product_label) {
      withoutProductLabel = customData.without_product_label
      // Replace [nom du produit] placeholder if present
      withoutProductLabel = withoutProductLabel.replace('[nom du produit]', productTitle)
    }

    if (customData.is_default_selected) {
      isDefaultSelected = true
    }
  }

  // Determine which option should be selected by default
  let sansSelected = !isDefaultSelected ? ' selected' : ''
  let avecSelected = isDefaultSelected ? ' selected' : ''

  // ========================================================================
  // HTML TEMPLATE GENERATION
  // Two different templates depending on context:
  // ========================================================================

  let dropdownElement, $select, $resetLink

  if (htmlTemplate === 'simple') {
    // ======================================================================
    // SIMPLE TEMPLATE (for wishlist cards)
    // Generates clean HTML matching variable product variations
    // No table, no reset link - just label + select
    // ======================================================================
    dropdownElement = $(`
      <div class="card-wishlist__variation-select">
        <label for="${selectId}">${dropdownTitle}</label>
        <select id="${selectId}" name="${selectName}">
          <option value="sans"${sansSelected}>${withoutProductLabel}</option>
          <option value="avec"${avecSelected}>${withProductLabel}</option>
        </select>
      </div>
    `)

    $dropdownTarget[mode](dropdownElement)
    $select = dropdownElement.find('select')
    $resetLink = null // No reset link in simple template

  } else {
    // ======================================================================
    // TABLE TEMPLATE (for product pages)
    // Generates WooCommerce-style table with reset link
    // Keeps original product page aesthetics
    // ======================================================================
    dropdownElement = $(`
      <table class="variations" cellspacing="0" role="presentation">
      <tbody>
      <tr class="attribute_options attribute_value_configurable" data-attribute_label="${dropdownTitle}">
      <td class="label">
      <label for="${selectId}">
      ${dropdownTitle}
      </label>
      </td>
      <td class="value">
      <select id="${selectId}" class="" name="${selectName}" data-attribute_name="attribute_produit_optionnel" data-show_option_none="yes">
      <option value="sans"${sansSelected}>${withoutProductLabel}</option>
      <option value="avec"${avecSelected}>${withProductLabel}</option>
      </select>
      <div class="reset_bundled_variations_fixed">
      <a class="reset_variations" href="#">Effacer</a>
      </div>
      </td>
      </tr>
      </tbody>
      </table>
    `)

    $dropdownTarget[mode](dropdownElement)
    $select = dropdownElement.find('select')
    $resetLink = dropdownElement.find('.reset_variations')
  }

  // ========================================================================
  // EVENT HANDLERS (same for both templates)
  // ========================================================================

  // When dropdown changes, update ALL checkboxes
  $select.on('change', function() {
    var selectedValue = $(this).val()
    if (selectedValue === 'avec') {
      $allCheckboxes.prop('checked', true)
    } else if (selectedValue === 'sans') {
      $allCheckboxes.prop('checked', false)
    }
    $allCheckboxes.trigger('change')
    onCheckboxChange()
  })

  // Reset link (only for table template)
  if ($resetLink) {
    $resetLink.on('click', function(e) {
      e.preventDefault()
      $select.val('sans').trigger('change')
    })
  }

  // Set initial state based on isDefaultSelected or current checkbox state
  if ($lastCheckbox.is(':checked')) {
    $select.val('avec')
  } else if (isDefaultSelected) {
    // If is_default_selected is true, check all checkboxes and set dropdown to 'avec'
    $allCheckboxes.prop('checked', true)
    $select.val('avec')
  }

  // When any checkbox changes, update the dropdown accordingly
  $allCheckboxes.on('change', function() {
    // Check if all checkboxes are checked
    let allChecked = true
    let allUnchecked = true

    $allCheckboxes.each(function() {
      if ($(this).is(':checked')) {
        allUnchecked = false
      } else {
        allChecked = false
      }
    })

    if (allChecked) {
      $select.val('avec')
    } else if (allUnchecked) {
      $select.val('sans')
    } else {
      // Mixed state - you might want to handle this differently
      $select.val('')
    }
  })

  return {
    $select: $select,
    $element: dropdownElement,
    mode: 'toggle',
    allCheckboxes: $allCheckboxes
  }
}
